import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import {
  designationNameFromOption,
  fetchDesignations,
  fetchIndustries,
  fetchSkillsByIndustry,
  industryOptionFromCompany,
  mapJobSkills,
  quickAddDesignation,
  quickAddSkill,
  splitSkillPayload,
} from "../utils/industryScopedMaster";

const uniqueByValue = (items) => {
  const seen = new Set();
  return (items || []).filter((item) => {
    const key = String(item?.value ?? "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export default function useIndustryScopedMaster({ language, sessionId }) {
  const [industryList, setIndustryList] = useState([]);
  const [industry, setIndustry] = useState(null);

  const [designation, setDesignation] = useState(null);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [designationPage, setDesignationPage] = useState(0);
  const [designationTotalPages, setDesignationTotalPages] = useState(0);
  const [designationLoading, setDesignationLoading] = useState(false);
  const [designationSearch, setDesignationSearch] = useState("");

  const [skills, setSkills] = useState([]);
  const [skillOptions, setSkillOptions] = useState([]);
  const [skillPage, setSkillPage] = useState(0);
  const [skillTotalPages, setSkillTotalPages] = useState(0);
  const [skillLoading, setSkillLoading] = useState(false);
  const [skillSearch, setSkillSearch] = useState("");

  const fitid = industry?.value > 0 ? industry.value : 0;

  useEffect(() => {
    fetchIndustries(language)
      .then(setIndustryList)
      .catch(() => setIndustryList([]));
  }, [language]);

  useEffect(() => {
    if (!industry?.value || industryList.length === 0) return;
    const match = industryList.find((item) => String(item.value) === String(industry.value));
    if (match && match.label && match.label !== industry.label) {
      setIndustry(match);
    }
  }, [industry, industryList]);

  const logDesignationSearch = useMemo(
    () =>
      debounce((typed) => {
        setDesignationSearch(typed || "");
      }, 250),
    []
  );

  const logSkillSearch = useMemo(
    () =>
      debounce((typed) => {
        setSkillSearch(typed || "");
      }, 250),
    []
  );

  const loadDesignations = useCallback(
    async (pageNumber = 1, search = designationSearch, replace = pageNumber === 1) => {
      if (!(fitid > 0)) return;
      setDesignationLoading(true);
      try {
        const { options, totalPages } = await fetchDesignations({
          fitid,
          search,
          pageNumber,
          language,
        });
        setDesignationPage(pageNumber);
        setDesignationTotalPages(totalPages);
        setDesignationOptions((prev) =>
          uniqueByValue(replace ? options : [...prev, ...options])
        );
      } catch {
        if (replace) setDesignationOptions([]);
      } finally {
        setDesignationLoading(false);
      }
    },
    [designationSearch, fitid, language]
  );

  const loadSkills = useCallback(
    async (pageNumber = 1, search = skillSearch, replace = pageNumber === 1) => {
      if (!(fitid > 0)) return;
      setSkillLoading(true);
      try {
        const { options, totalPages } = await fetchSkillsByIndustry({
          fitid,
          search,
          pageNumber,
          language,
        });
        setSkillPage(pageNumber);
        setSkillTotalPages(totalPages);
        setSkillOptions((prev) =>
          uniqueByValue(replace ? options : [...prev, ...options])
        );
      } catch {
        if (replace) setSkillOptions([]);
      } finally {
        setSkillLoading(false);
      }
    },
    [fitid, language, skillSearch]
  );

  const resetDependents = useCallback(() => {
    setDesignation(null);
    setDesignationOptions([]);
    setDesignationPage(0);
    setDesignationTotalPages(0);
    setDesignationSearch("");
    setSkills([]);
    setSkillOptions([]);
    setSkillPage(0);
    setSkillTotalPages(0);
    setSkillSearch("");
  }, []);

  const onIndustryChange = useCallback(
    (option) => {
      setIndustry(option);
      resetDependents();
    },
    [resetDependents]
  );

  const applyCompanyIndustry = useCallback(
    (company, { resetIfChanged = true } = {}) => {
      const next = industryOptionFromCompany(company, industryList);
      if (!next) return;
      const changed = String(industry?.value || "") !== String(next.value);
      setIndustry(next);
      if (resetIfChanged && changed) {
        resetDependents();
      }
    },
    [industry, industryList, resetDependents]
  );

  const hydrateFromJob = useCallback((jobData, skillNames) => {
    if (jobData?.industryId) {
      setIndustry({
        value: jobData.industryId,
        label: jobData.industryName || "Industry",
      });
    }
    if (jobData?.designationName) {
      setDesignation({
        value: jobData.designationId || jobData.designationName,
        label: jobData.designationName,
      });
    }
    setSkills(mapJobSkills(jobData, skillNames));
  }, []);

  useEffect(() => {
    if (fitid > 0) {
      loadDesignations(1, designationSearch, true);
    }
  }, [fitid, designationSearch, loadDesignations]);

  useEffect(() => {
    if (fitid > 0) {
      loadSkills(1, skillSearch, true);
    }
  }, [fitid, skillSearch, loadSkills]);

  const createDesignation = async (inputValue) => {
    const option = await quickAddDesignation({
      name: inputValue,
      fitid,
      sessionId,
      language,
    });
    setDesignationOptions((prev) => uniqueByValue([...prev, option]));
    setDesignation(option);
    return option;
  };

  const createSkill = async (inputValue) => {
    const option = await quickAddSkill({
      name: inputValue,
      fitid,
      sessionId,
      language,
    });
    setSkillOptions((prev) => uniqueByValue([...prev, option]));
    setSkills((prev) => uniqueByValue([...(Array.isArray(prev) ? prev : []), option]));
    return option;
  };

  const resetMaster = useCallback(() => {
    setIndustry(null);
    resetDependents();
  }, [resetDependents]);

  const jobMasterPayload = () => {
    const { skillIds, skillNames } = splitSkillPayload(skills);
    return {
      industryId: fitid || undefined,
      designationName: designationNameFromOption(designation),
      skillIds,
      skillNames,
    };
  };

  return {
    industryList,
    industry,
    setIndustry,
    fitid,
    onIndustryChange,
    applyCompanyIndustry,
    hydrateFromJob,
    designation,
    setDesignation,
    designationOptions,
    designationLoading,
    onDesignationMenuOpen: () => {
      if (fitid > 0 && designationOptions.length === 0) {
        loadDesignations(1, designationSearch, true);
      }
    },
    onDesignationInputChange: (typed) => {
      logDesignationSearch(typed);
      if (!typed) setDesignationPage(0);
    },
    onDesignationMenuScroll: () => {
      if (designationPage < designationTotalPages) {
        loadDesignations(designationPage + 1, designationSearch, false);
      }
    },
    createDesignation,
    skills,
    setSkills,
    skillOptions,
    skillLoading,
    onSkillMenuOpen: () => {
      if (fitid > 0 && skillOptions.length === 0) {
        loadSkills(1, skillSearch, true);
      }
    },
    onSkillInputChange: (typed) => {
      logSkillSearch(typed);
      if (!typed) setSkillPage(0);
    },
    onSkillMenuScroll: () => {
      if (skillPage < skillTotalPages) {
        loadSkills(skillPage + 1, skillSearch, false);
      }
    },
    createSkill,
    resetMaster,
    jobMasterPayload,
  };
}
